import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Params } from '@angular/router';
import { apis } from '../config/api';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

/**
 * Package usage for a booking. Delivered inline with whatever describes
 * the booking — `usage` on each row of POST /lessons/:schoolId/check-availability,
 * and `usage` on GET /bookings/:schoolId/:bookingId — rather than from a
 * dedicated endpoint, so it can never disagree with the rows it annotates.
 */
export interface BookingUsage {
    bookingId: string;
    studentId: string;
    currency: string;
    productTitle: string | null;
    purchasedMinutes: number;
    completedMinutes: number;
    scheduledMinutes: number;
    /** Negative when the booking is already committed past what it bought. */
    remainingMinutes: number;
    remainingHours: number;
    isOverbooked: boolean;
    overageMinutes: number;
    discountPercent: number | null;
    charged: number;
    paid: number;
    toPay: number;
    /**
     * True when this booking was auto-upgraded and its usage has since
     * dropped back within the plan it was upgraded from — the upgrade is
     * no longer justified. Not corrected automatically, because reverting
     * would raise the per-hour rate on the remaining lessons.
     */
    planMismatch: boolean;
    /** The plan it was upgraded from — feeds the change-plan preview. */
    revertToPriceRowId: string | null;
}

/** GET /v1/bookings/:schoolId/:bookingId/change-plan/preview */
export interface PlanChangePreview {
    bookingId: string;
    consumedMinutes: number;
    lessonsAffected: number;
    currentPlan: { priceRowId: string; price: number | null; durationUnit: number; durationValue: number };
    newPlan: { priceRowId: string; price: number; durationUnit: number; durationValue: number };
    currency: string;
    chargedTotal: number;
    recomputedOldTotal: number;
    newTotal: number;
    /** Negative = student ends up owing less. */
    delta: number;
    perLesson: { lessonId: string; minutes: number; amount: number }[];
}

@Injectable({
    providedIn: 'root'
})

export class BookingsService {

    http = inject(HttpClient);
    url = apis.bookings;



    getCalenderSummary(params: Params, schoolId: string): Observable<any> {
        const httpParams = new HttpParams({ fromObject: params });
        return this.http.get(`${this.url}/${schoolId}/booking-summary`, { params: httpParams });
    }
    getCalenderDaySummary(params: Params, schoolId: string): Observable<any> {
        const httpParams = new HttpParams({ fromObject: params });
        return this.http.get(`${this.url}/${schoolId}/booking-summary/day`, { params: httpParams });
    }
    

    getBookings(params: Params, schoolId: string): Observable<any> {
        const httpParams = new HttpParams({ fromObject: params });
        return this.http.get(`${this.url}/${schoolId}`, { params: httpParams });
    }
    
    getBookingDetail(schoolId: string, bookingId: string): Observable<any> {
        return this.http.get(`${this.url}/${schoolId}/${bookingId}`);
    }

    createBooking(schoolId: string, body: any): Observable<any> {
        return this.http.post(`${this.url}/${schoolId}`, body);
    }

    updateBooking(id: string,schoolId: string, params: Params): Observable<any> {
        return this.http.patch(`${this.url}/${schoolId}/${id}`, params);
    }

    // Front desk cancellation: cancels the booking AND releases any lesson it
    // was the last participant of. Differs from updateBooking(status=CANCELLED),
    // which leaves lessons scheduled.
    cancelBooking(schoolId: string, bookingId: string): Observable<any> {
        return this.http.post(`${this.url}/${schoolId}/${bookingId}/cancel`, {});
    }

    deleteBooking(bookingId: string, schoolId: string): Observable<any> {
        const url = `${this.url}/${schoolId}/${bookingId}`;
        return this.http.delete(url);
    }

    /** What a plan shift would cost. Writes nothing. */
    previewPlanChange(schoolId: string, bookingId: string, priceRowId: string): Observable<PlanChangePreview> {
        return this.http.get<PlanChangePreview>(
            `${this.url}/${schoolId}/${bookingId}/change-plan/preview`,
            { params: new HttpParams().set('priceRowId', priceRowId) },
        );
    }

    /** Move the booking to another package and re-bill consumed lessons. */
    changePlan(schoolId: string, bookingId: string, priceRowId: string, note?: string): Observable<any> {
        return this.http.post(`${this.url}/${schoolId}/${bookingId}/change-plan`, { priceRowId, note });
    }

    getPlanChanges(schoolId: string, bookingId: string): Observable<{ results: any[] }> {
        return this.http.get<{ results: any[] }>(`${this.url}/${schoolId}/${bookingId}/change-plan`);
    }

    /** Switch booking activity (e.g. Kite -> Wind Surfing) */
    changeActivity(
        schoolId: string,
        bookingId: string,
        payload: {
            productId: string;
            priceRowId: string;
            levelId?: string | null;
            hours?: number | null;
            note?: string | null;
        }
    ): Observable<any> {
        return this.http.post(`${this.url}/${schoolId}/${bookingId}/change-activity`, payload);
    }

    completeEnrollment(payload: any, token: string) {
        const url = (window as any).apis?.studentBooking || '/api/student-booking';
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        });
        return this.http.post(url, payload, { headers }).toPromise();
    }
}